import { useState } from "react";
import { TeacherQrOverlay } from "./TeacherChrome";
import { navigate } from "../lib/navigation";
import { studioExitTarget } from "./StudioShell";
import { clearSession, type StudentSession } from "../lib/session";

/**
 * 시연작 교사 바 — 한결 DESIGN-POLICY §9.H-14 B형.
 *
 * 🔑 **학생 화면 = 교사 화면의 부분집합.** 이 바는 학생 화면 «위에» 얹히기만 하고
 * 학생 페이지 컴포넌트는 한 줄도 바꾸지 않는다. 교사는 학생이 보는 것을 그대로 보면서
 * 컨트롤(QR·현황·나가기)만 더 갖는다.
 *
 * 🚨 QR 버튼은 조건부로 숨기지 않는다(§9.H-14 결정 B). 시연 세션은 코드가 발급된 뒤에만
 * 이 화면에 닿으므로 코드는 항상 존재한다.
 *
 * 「시연 끝내기」는 교사의 로컬 참여 상태만 비우고 **그 수업의 현황으로** 되돌린다.
 *
 * 🚨 **2026-08-12 개정(B1)** — 시연은 세션이 아니라 «이 수업 안의 작품» 이다.
 *    그래서 만든 시연작은 그대로 남고, 다시 누르면 **이어서** 만든다
 *    (서버가 같은 참여자를 돌려준다). 종전의 「매번 목록부터 새로 만든다」(§9.H-14 v1.8)는
 *    시연이 독립 수업이던 시절의 규칙이고, 그게 곧 수업이 누적되던 원인이었다.
 */
export function TeacherDemoBar({ session }: { session: StudentSession }) {
  const [qrOpen, setQrOpen] = useState(false);
  const label = session.kind === "mv"
    ? "영상 만들기"
    : session.kind === "image" ? "그림 만들기" : session.kind === "poster" ? "포스터 만들기" : "음악 만들기";

  const leave = (): void => {
    // 🚨 이 시연작 하나만 지운다 — 다른 탭의 시연·학생 화면은 그대로 산다(2026-10-12).
    clearSession("demo-exit", undefined, session.workId);
    // 🔑 온 곳으로 되돌린다 = 시연을 켠 그 수업의 현황(B1, 2026-08-12).
    //    StudioShell 의 나가는 문과 **같은 곳**이어야 한다 — 한쪽만 고치면 같은 사고가 다시 난다.
    // 🔑 목적지를 여기서 «다시 계산하지 않는다» — studioExitTarget 하나가 정한다.
    //    2026-08-07 사고의 진짜 원인이 「나가는 문이 둘인데 각자 계산한 것」이었다.
    //    같은 함수를 부르면 어긋날 수가 없다.
    navigate(studioExitTarget(session).path);
  };

  return (
    <>
      <div className="teacher-demo-bar" role="region" aria-label="시연작 진행 중">
        <div className="teacher-demo-bar-info">
          <span className="teacher-demo-badge">시연작</span>
          <span className="teacher-demo-title">{label}</span>
          <span className="teacher-demo-code">참여 코드 <b>{session.code}</b></span>
        </div>
        <div className="teacher-demo-bar-actions">
          <button type="button" className="button-secondary" onClick={() => setQrOpen(true)}>
            참여 QR
          </button>
          <button
            type="button"
            className="button-secondary"
            disabled={!session.demoSessionId}
            onClick={() => {
              if (session.demoSessionId) {
                navigate(`/teacher/live/${encodeURIComponent(session.demoSessionId)}`);
              }
            }}
          >
            학생 현황
          </button>
          <button type="button" className="button-ghost" onClick={leave}>
            시연 끝내기
          </button>
        </div>
      </div>
      {qrOpen ? (
        <TeacherQrOverlay
          title={`시연작 · ${label}`}
          code={session.code}
          onClose={() => setQrOpen(false)}
        />
      ) : null}
    </>
  );
}
